import { useState, useEffect, useCallback, useRef, memo } from "react";
import { Sparkles } from "lucide-react";

interface Product {
  id: number;
  name: string;
  emoji: string;
  before: string;
  after: string;
}

// Use static paths instead of imports to avoid bundling all images upfront
const products: Product[] = [
  { id: 1, name: "Calça", emoji: "👖", before: "/gallery/calca-before.webp", after: "/gallery/calca-after.jpg" },
  { id: 2, name: "Conjunto", emoji: "👚", before: "/gallery/conjunto-before.webp", after: "/gallery/conjunto-after.jpg" },
  { id: 3, name: "Jaqueta", emoji: "🧥", before: "/gallery/jaqueta-before.webp", after: "/gallery/jaqueta-after.jpg" },
  { id: 4, name: "Camiseta", emoji: "👕", before: "/gallery/camiseta-before.webp", after: "/gallery/camiseta-after.jpg" },
];

const ProductCard = memo(function ProductCard({ 
  product, 
  isActive, 
  isVisible,
  onClick 
}: { 
  product: Product; 
  isActive: boolean; 
  isVisible: boolean;
  onClick: () => void;
}) {
  return (
    <div
      className="group cursor-pointer perspective-1000"
      onClick={onClick}
    >
      {/* 3D Flip Card */}
      <div 
        className={`relative transform-style-3d transition-transform duration-500 ease-out will-change-transform ${
          isActive ? 'rotate-y-180' : ''
        }`}
      >
        {/* Front Side - Product */}
        <div 
          className={`relative rounded-2xl overflow-hidden backface-hidden transition-shadow duration-300 ${
            isActive ? 'invisible' : 'visible'
          } ring-1 ring-border hover:ring-primary/50 hover:shadow-glow bg-card`}
        >
          {/* Status badge */}
          <div className="absolute top-3 right-3 z-10">
            <div className="px-2.5 py-1 rounded-full text-xs font-medium bg-secondary/80 text-foreground backdrop-blur-sm">
              Produto
            </div>
          </div>

          <div className="aspect-[3/4] relative bg-secondary">
            {isVisible && (
              <img 
                src={product.before}
                alt={`${product.name} - produto`}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            )}
          </div>

          {/* Product name badge */}
          <div className="absolute bottom-3 left-3">
            <div className="gradient-primary px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-md">
              <Sparkles className="w-3 h-3 text-white" />
              <span className="text-xs font-medium text-white">{product.name}</span>
            </div>
          </div>
        </div>

        {/* Back Side - Model */}
        <div 
          className={`absolute inset-0 rounded-2xl overflow-hidden backface-hidden rotate-y-180 ${
            isActive ? 'visible' : 'invisible'
          } ring-2 ring-primary shadow-glow`}
          style={{
            background: 'linear-gradient(180deg, hsl(340, 40%, 25%) 0%, hsl(340, 30%, 15%) 100%)'
          }}
        >
          {/* Status badge */}
          <div className="absolute top-3 right-3 z-10">
            <div className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary text-white">
              Modelo
            </div>
          </div>

          <div className="aspect-[3/4] relative">
            {(isVisible && isActive) && (
              <img 
                src={product.after}
                alt={`${product.name} - modelo`}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            )}
          </div>

          {/* Product name badge */}
          <div className="absolute bottom-3 left-3">
            <div className="gradient-primary px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-md">
              <Sparkles className="w-3 h-3 text-white" />
              <span className="text-xs font-medium text-white">{product.name}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export const MagicSection = memo(function MagicSection() {
  const [activeProduct, setActiveProduct] = useState<number | null>(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  // IntersectionObserver to only load images when section is in viewport
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Auto-rotate through products
  useEffect(() => {
    if (!isAutoPlaying || !isVisible) return;

    const interval = setInterval(() => {
      setActiveProduct((current) => {
        if (current === null) return 1;
        if (current >= products.length) return null;
        return current + 1;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, isVisible]);

  // Pause auto-play on user interaction
  const handleClick = useCallback((productId: number) => {
    setIsAutoPlaying(false);
    setActiveProduct((current) => (current === productId ? null : productId));
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-card/50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Veja a <span className="text-gradient">mágica</span> acontecer
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Clique em qualquer produto para ver a transformação
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {products.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              isActive={activeProduct === product.id}
              isVisible={isVisible}
              onClick={() => handleClick(product.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
});
